/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */

export enum ClockDialView {
  Hours = 'hours',
  Minutes = 'minutes'
}

export const CLOCK_HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
export const CLOCK_MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

const CLOCK_CENTER = 128;
const CLOCK_RADIUS = 104;
const CLOCK_OPTION_RADIUS = 24;

export interface ClockPosition {
  left: number;
  top: number;
}

export interface ClockOptionSelection {
  hour: number;
  minute: number;
  value: number;
  view: ClockDialView | null;
}

/**
 * Calculates the absolute option button position for a 12-step clock dial.
 *
 * @param index Zero-based clock index where 0 is top-center.
 * @returns The left and top offset for the option button.
 */
export const getClockOptionPosition = (index: number): ClockPosition => {
  const angle = (index / 12) * 2 * Math.PI;
  return {
    left : CLOCK_CENTER + (CLOCK_RADIUS * Math.sin(angle)) - CLOCK_OPTION_RADIUS,
    top  : CLOCK_CENTER - (CLOCK_RADIUS * Math.cos(angle)) - CLOCK_OPTION_RADIUS
  };
};

/**
 * Converts a pointer coordinate into a 12-step clock index.
 *
 * @param element The clock face element used for bounds.
 * @param clientX The pointer x coordinate.
 * @param clientY The pointer y coordinate.
 * @returns A clock index from 0 to 11.
 */
export const getClockIndexFromPoint = (
  element: HTMLElement,
  clientX: number,
  clientY: number
): number => {
  const {left, top, width, height} = element.getBoundingClientRect();
  const centerX = left + (width / 2);
  const centerY = top + (height / 2);
  const radians = Math.atan2(clientX - centerX, centerY - clientY);
  const normalized = radians < 0 ? radians + (2 * Math.PI) : radians;
  return Math.round((normalized / (2 * Math.PI)) * 12) % 12;
};

/**
 * Converts a pointer coordinate into an exact minute value.
 *
 * @param element The clock face element used for bounds.
 * @param clientX The pointer x coordinate.
 * @param clientY The pointer y coordinate.
 * @returns A minute value from 0 to 59.
 */
export const getClockMinuteFromPoint = (
  element: HTMLElement,
  clientX: number,
  clientY: number
): number => {
  const {left, top, width, height} = element.getBoundingClientRect();
  const centerX = left + (width / 2);
  const centerY = top + (height / 2);
  const radians = Math.atan2(clientX - centerX, centerY - clientY);
  const normalized = radians < 0 ? radians + (2 * Math.PI) : radians;
  return Math.round((normalized / (2 * Math.PI)) * 60) % 60;
};

/**
 * Resolves the clock hand rotation from the active view and selected value.
 *
 * @param view The active clock view.
 * @param hour The selected hour.
 * @param minute The selected minute.
 * @returns CSS transform rotation for the clock hand.
 */
export const getClockPointerTransform = (
  view: ClockDialView | null,
  hour: number,
  minute: number
): string => {
  const degrees = view === ClockDialView.Hours
    ? (CLOCK_HOURS.indexOf(hour) >= 0 ? CLOCK_HOURS.indexOf(hour) * 30 : 0)
    : minute * 6;
  return `rotate(${degrees}deg)`;
};

/**
 * Calculates where to render the selected-value drag handle.
 *
 * @param view The active clock view.
 * @param hour The selected hour.
 * @param minute The selected minute.
 * @returns The left and top offset for the selected handle.
 */
export const getSelectedHandlePosition = (
  view: ClockDialView | null,
  hour: number,
  minute: number
): ClockPosition => {
  const index = view === ClockDialView.Hours
    ? CLOCK_HOURS.indexOf(hour)
    : minute / 5;
  const angle = ((index >= 0 ? index : 0) / 12) * 2 * Math.PI;
  return {
    left : CLOCK_CENTER + (CLOCK_RADIUS * Math.sin(angle)) - CLOCK_OPTION_RADIUS,
    top  : CLOCK_CENTER - (CLOCK_RADIUS * Math.cos(angle)) - CLOCK_OPTION_RADIUS
  };
};

/**
 * Checks whether a pointer event began on a dial option button.
 *
 * @param target The event target to inspect.
 * @returns True when the target is part of a clock option.
 */
export const isClockOptionEvent = (target: EventTarget | null): boolean => (
  target instanceof HTMLElement && !!target.closest('[data-clock-option]')
);

/**
 * Returns the option list for the active clock view.
 *
 * @param view The active clock view.
 * @returns Hour options for hour view, otherwise minute options.
 */
export const getClockOptions = (view: ClockDialView | null): number[] => (
  view === ClockDialView.Hours ? CLOCK_HOURS : CLOCK_MINUTES
);

/**
 * Formats a visible clock option label.
 *
 * @param view The active clock view.
 * @param value The numeric option value.
 * @returns Hours as numbers and minutes as two-digit text.
 */
export const getClockOptionLabel = (view: ClockDialView | null, value: number): number | string => (
  view === ClockDialView.Hours ? value : value.toString().padStart(2, '0')
);

/**
 * Formats the selected drag-handle label.
 *
 * @param view The active clock view.
 * @param hour The selected hour.
 * @param minute The selected minute.
 * @returns The selected hour or two-digit minute label.
 */
export const getSelectedClockLabel = (
  view: ClockDialView | null,
  hour: number,
  minute: number
): number | string => (
  view === ClockDialView.Hours ? hour : minute.toString().padStart(2, '0')
);

/**
 * Determines whether a visible option is selected.
 *
 * @param view The active clock view.
 * @param value The option value.
 * @param hour The selected hour.
 * @param minute The selected minute.
 * @returns True when the option matches the selected clock value.
 */
export const isClockOptionSelected = ({
  hour,
  minute,
  value,
  view
}: ClockOptionSelection): boolean => (
  view === ClockDialView.Hours ? value === hour : value === minute
);
